/* Live check: polls the device list every 5 s until the device talks to StartHR. */
import { useState } from "react";
import type { Device } from "../../../data/types";
import { useDevices } from "../../../data/hooks";
import { Button, Icon, errMsg, useToast } from "../../../ui";
import { useI18n } from "../../../i18n";
import { canDemo, useDemoPunch } from "./demo";
import { hm } from "./labels";

export const LIVE_POLL_MS = 5000;

export function LiveCheck({ device, token, testOk, onSkip }: { device: Device; token?: string | null; testOk?: boolean; onSkip?: () => void }) {
  const { L, name } = useI18n();
  const toast = useToast();
  const devices = useDevices({ refetchInterval: LIVE_POLL_MS });
  const demo = useDemoPunch();
  const [base] = useState(() => ({ lastSeen: device.lastSeen, lastPunchAt: device.lastPunchAt }));
  const [busy, setBusy] = useState(false);
  const cur = devices.data?.find((d) => d.id === device.id) ?? device;
  const changed = cur.lastSeen !== base.lastSeen || cur.lastPunchAt !== base.lastPunchAt;
  const ok = !!testOk || changed;

  const sendDemo = async () => {
    setBusy(true);
    try {
      const r = await demo.send(cur, token);
      const e = r.employee;
      toast(e ? L(`Test punch received for ${e.nameEn} — attendance updated`, `وصلت بصمة تجريبية لـ ${name(e)} — تم تحديث الحضور`) : L("Test punch received", "وصلت البصمة التجريبية"));
    } catch (err) { toast(errMsg(err), "bad"); } finally { setBusy(false); }
  };

  return (
    <div className={"live" + (ok ? " ok" : "")} data-testid="live-check" data-state={ok ? "ok" : "waiting"} aria-live="polite">
      {ok ? <span className="ok-ico"><Icon name="check" /></span> : <span className="spin" aria-hidden="true" />}
      <div className="live-b">
        {ok ? (
          <>
            <b data-testid="live-ok">{cur.lastPunchAt && cur.lastPunchAt !== base.lastPunchAt
              ? L(`Connected — last punch ${hm(cur.lastPunchAt)}`, `متصل — آخر بصمة ${hm(cur.lastPunchAt)}`)
              : L("Connected — StartHR can reach this device", "متصل — ستارت HR يتواصل مع الجهاز")}</b>
            <span>{L("Punches now flow into attendance automatically.", "تنتقل البصمات الآن إلى الحضور تلقائيًا.")}</span>
          </>
        ) : (
          <>
            <b>{L("Waiting for the first punch…", "بانتظار أول بصمة…")}</b>
            <span>{device.protocol === "poll"
              ? L("Use Test connection, or wait for the next sync.", "استخدم «اختبار الاتصال» أو انتظر المزامنة التالية.")
              : L("Ask someone to punch in on the device. This box turns green by itself.", "اطلب من أحد الموظفين البصم على الجهاز. سيتحول هذا المربع إلى الأخضر تلقائيًا.")}</span>
          </>
        )}
      </div>
      <div className="live-actions">
        {demo.enabled && canDemo(cur, token) && (
          <Button variant="sm" icon="play" onClick={sendDemo} loading={busy} data-testid="btn-demo-punch">{L("Send a test punch (demo)", "إرسال بصمة تجريبية (عرض)")}</Button>
        )}
        {!ok && onSkip && <Button variant="ghost sm" onClick={onSkip} data-testid="btn-skip">{L("Skip for now", "تخطَّ الآن")}</Button>}
      </div>
    </div>
  );
}
